import React from 'react';
import styled from 'styled-components';
import { FormattedMessage, defineMessage } from 'react-intl';
import { Button, Layout, Tag } from 'antd';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons';
import TrialBanner from './TrialBanner';

const HeaderContainer = styled(Layout.Header)`
  background: #fff;
  display: flex;
  justify-content: space-between;
  padding: 0;
`;

export const SquareIconButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 5px 8px;
  border-radius: 6px;
  border-width: 1px;
`;

const styleMenuIcon = (icon: typeof MenuUnfoldOutlined) => styled(icon)`
  font-size: 20px;
  line-height: 64px;
  padding: 0 24px;
  cursor: pointer;
  transition: color 0.3s;

  &:hover {
    color: #233456;
  }
`;

const StyledMenuUnfoldOutlined = styleMenuIcon(MenuUnfoldOutlined);
const StyledMenuFoldOutlined = styleMenuIcon(MenuFoldOutlined);

const StyledScreenTitle = styled.span`
  display: inline-block;
  font-size: 17px;
  text-align: left;
  width: calc(100% - 80px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const screenTitleMsgs = {
  'start-page': defineMessage({ defaultMessage: 'Home' }),
  'inventory/products': defineMessage({
    defaultMessage: 'Inventory / Products',
  }),
  'inventory/services': defineMessage({
    defaultMessage: 'Inventory / Services',
  }),
  'inventory/giftcards': defineMessage({
    defaultMessage: 'Inventory / Giftcards',
  }),
  'inventory/orders-suggestions': defineMessage({
    defaultMessage: 'Inventory / Purchase order suggestion',
  }),
  'inventory/stocktaking': defineMessage({
    defaultMessage: 'Inventory / Stocktaking',
  }),
  'inventory/categories': defineMessage({
    defaultMessage: 'Inventory / Categories ',
  }),
  'contacts/suppliers': defineMessage({
    defaultMessage: 'Contacts / Suppliers',
  }),
  'settings/users': defineMessage({ defaultMessage: 'Settings / Users' }),
  contacts: defineMessage({ defaultMessage: 'Contacts' }),
  'integrations/sage': defineMessage({
    defaultMessage: 'Integrations / Sage / Welcome',
  }),
  'integrations/sage/settings': defineMessage({
    defaultMessage: 'Integrations / Sage / Settings',
  }),
  'integrations/sage/cashbook': defineMessage({
    defaultMessage: 'Integrations / Sage / Cashbook',
  }),
  'integrations/sage/callback': defineMessage({
    defaultMessage: 'Integrations / Sage / Callback',
  }),
  'integrations/sage/logs': defineMessage({
    defaultMessage: 'Integrations / Sage / Logs',
  }),
  'settings/rksv': defineMessage({ defaultMessage: 'Settings / RKSV' }),
  'integrations/rksv/logs': defineMessage({ defaultMessage: 'Logs' }),
  'integrations/rksv/dep-export': defineMessage({
    defaultMessage: 'DEP Export',
  }),
  'integrations/rksv/failed-receipts': defineMessage({
    defaultMessage: 'Failed Receipts',
  }),
  'integrations/rksv/settings': defineMessage({ defaultMessage: 'Settings' }),
  'settings/datev/home': defineMessage({
    defaultMessage: 'Settings / Datev',
  }),
  // The DATEV OAuth app still redirects to /integrations/datev/callback
  // (see Datev/config.ts) — keep this key so that screen still gets a title.
  'integrations/datev/callback': defineMessage({
    defaultMessage: 'Integrations / Callback',
  }),
  'settings/kassensichv': defineMessage({
    defaultMessage: 'Settings / KassenSichV',
  }),
  'settings/general': defineMessage({
    defaultMessage: 'Settings / General',
  }),
  'settings/e-commerce': defineMessage({
    defaultMessage: 'Settings / E-commerce',
  }),
  'settings/store': defineMessage({ defaultMessage: 'Settings / Store' }),
  'settings/accounting': defineMessage({
    defaultMessage: 'Settings / Accounting',
  }),
  'settings/customer-display': defineMessage({
    defaultMessage: 'Settings / Customer Display',
  }),
  imprint: defineMessage({ defaultMessage: 'Imprint' }),
  'reports/dashboard': defineMessage({ defaultMessage: 'Reports / Dashboard' }),
  'reports/live': defineMessage({ defaultMessage: 'Reports / Live reports' }),
  'reports/cashbook': defineMessage({
    defaultMessage: 'Reports / Cashbook',
  }),
  'reports/downloads': defineMessage({ defaultMessage: 'Reports / Downloads' }),
  'shorepay/account': defineMessage({
    defaultMessage: 'Shore Pay / My Account',
  }),
  'shorepay/readers': defineMessage({
    defaultMessage: 'Shore Pay / Readers',
  }),
  'shorepay/reports': defineMessage({
    defaultMessage: 'Shore Pay / Reports',
  }),
  'shorepay/settings': defineMessage({
    defaultMessage: 'Shore Pay / Settings',
  }),
};

export type ScreenTitle = keyof typeof screenTitleMsgs;

type Props = {
  screenName: ScreenTitle;
  collapsed: boolean;
  trialDaysLeft: number | null;
  toggleMenu: () => void;
  handleActivateClick: () => void;
  isfetchingData: boolean;
  isAccountActive: boolean;
  hasNotActiveTSE: boolean;
  isATUserWithMissingCountry: boolean;
  isContractAccepted: boolean;
};

const TopMenu: React.FC<React.PropsWithChildren<Props>> = ({
  screenName,
  collapsed,
  toggleMenu,
  trialDaysLeft,
  handleActivateClick,
  children,
  isAccountActive,
  isfetchingData,
  hasNotActiveTSE,
  isATUserWithMissingCountry,
  isContractAccepted,
}) => (
  <HeaderContainer>
    <div css="display: flex; flex-grow: 1">
      {collapsed ? (
        <StyledMenuUnfoldOutlined onClick={toggleMenu} />
      ) : (
        <StyledMenuFoldOutlined onClick={toggleMenu} />
      )}
      <StyledScreenTitle>
        {Object.keys(screenTitleMsgs).includes(screenName) ? (
          <FormattedMessage {...screenTitleMsgs[screenName]} />
        ) : (
          screenName
        )}
      </StyledScreenTitle>
    </div>
    {!isfetchingData && hasNotActiveTSE && (
      <div
        css="display: flex; flex-grow: 1; 
        padding:10px
      "
      >
        <Tag
          color="warning"
          css={`
            text-align: center;
            padding: 12px;
            border-radius: 4px;
          `}
          icon={<ExclamationCircleFilled />}
        >
          <span
            style={{
              padding: '1px',
            }}
          >
            <FormattedMessage
              defaultMessage="Ensure fiscal compliance by activating<ksvLink>TSE</ksvLink>. Failure to do so may result in penalties"
              values={{
                ksvLink: (...txt: React.ReactNode[]) => (
                  <a
                    href="/settings/kassensichv"
                    rel="noopener noreferrer"
                    css="margin: 0px 3px; text-decoration: underline;color: #fa8c16"
                  >
                    <b>{txt}</b>
                  </a>
                ),
              }}
            />
          </span>
        </Tag>
      </div>
    )}
    {!isfetchingData && isATUserWithMissingCountry && (
      <div
        css="display: flex; flex-grow: 1; 
        padding:10px
      "
      >
        <Tag
          color="warning"
          css={`
            text-align: center;
            padding: 12px;
            border-radius: 4px;
          `}
          icon={<ExclamationCircleFilled />}
        >
          <span
            style={{
              padding: '1px',
            }}
          >
            <FormattedMessage
              defaultMessage="<bold>Action Needed</bold>: Update your “Country” value in shipping and billing addresses. <locationLink>Click</locationLink> here"
              values={{
                locationLink: (...txt: React.ReactNode[]) => (
                  <a
                    href="/settings/store?_=1"
                    rel="noopener noreferrer"
                    css="margin: 0px 3px; text-decoration: underline;color: #fa8c16"
                  >
                    <b>{txt}</b>
                  </a>
                ),
                bold: (...txt: React.ReactNode[]) => <b>{txt}</b>,
              }}
            />
          </span>
        </Tag>
      </div>
    )}
    <div css="display: flex; flex-grow: 1; justify-content: flex-end; align-items: center; gap: 4px; padding-right: 24px;">
      {trialDaysLeft !== null && (
        <TrialBanner
          daysLeft={trialDaysLeft}
          handleClick={handleActivateClick}
          isContractAccepted={isContractAccepted}
        />
      )}
      {isAccountActive !== undefined && !isAccountActive && (
        <TrialBanner
          handleClick={handleActivateClick}
          isAccountActive={isAccountActive}
          isContractAccepted={isContractAccepted}
        />
      )}
      {children}
    </div>
  </HeaderContainer>
);

export default TopMenu;
